import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import api from '../services/api';
import { User } from '../types';

// --- DEFINIÇÃO DE TIPOS (INTERFACES) ---

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (loginValue: string, password: string) => Promise<void>;
  logout: () => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

// --- CRIAÇÃO DO CONTEXTO ---
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * @component AuthProvider
 * @description O Provedor de Contexto que envolve a aplicação e gere o estado de autenticação.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        api.defaults.headers.Authorization = `Bearer ${token}`;
        try {
          const response = await api.get('/user');
          setUser(response.data);
        } catch (error) {
          console.error("Token inválido, a limpar...", error);
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  const login = async (loginValue: string, password: string) => {
    const response = await api.post('/login', { login: loginValue, password });
    const { user: userData, token: newToken } = response.data;
    
    localStorage.setItem('token', newToken);
    api.defaults.headers.Authorization = `Bearer ${newToken}`;
    setUser(userData);
    setToken(newToken);
  };

  const logout = () => {
    api.post('/logout').finally(() => {
      localStorage.removeItem('token');
      delete api.defaults.headers.Authorization;
      setUser(null);
      setToken(null);
    });
  };

  // O valor `isAuthenticated` é derivado diretamente da existência do objeto `user`.
  const isAuthenticated = !!user;

  // Não renderiza a aplicação enquanto o token está a ser verificado.
  if (loading) {
    return <div>A verificar autenticação...</div>;
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}; // <--- A CHAVE DE FECHO QUE FALTAVA ESTÁ AQUI

/**
 * @hook useAuth
 * @description Um hook personalizado para facilitar o acesso ao contexto de autenticação.
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};
